/**
 * A standalone meeting's agenda (TMMAPP-0159 §D 22): the meeting's description, shown first in its
 * session. Every writer edits it (`PUT /v1/me/meetings/{id}/agenda`, TMMAPI-0188 G1), not only the
 * organizer; blank clears it. A Read-only guest reads it.
 */
import { useState } from 'react';
import { Alert, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meetingsApi } from '@/api/meetings';
import { HeadedCard } from '@/components/HeadedCard';
import { SubmitButton } from '@/components/SubmitButton';
import { resolveTokens } from '@/theme/theme';

const AGENDA_MAX = 4000;

interface SessionAgendaProps {
  meetingId: number;
  agenda: string | null;
  canWrite: boolean;
  onChanged: () => void;
}

export function SessionAgenda({ meetingId, agenda, canWrite, onChanged }: SessionAgendaProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    try {
      await meetingsApi.putAgenda(meetingId, draft.trim());
      setDraft(null);
      onChanged();
    } catch {
      setError(t('meetings.errorGeneric'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <HeadedCard
      title={t('meetings.session.agenda')}
      subtitle={t('meetings.session.agendaSub')}
      id="session-agenda"
      actions={
        canWrite && draft === null ? (
          <Button size="small" onClick={() => setDraft(agenda ?? '')}>
            {t('meetings.session.agendaEdit')}
          </Button>
        ) : undefined
      }
    >
      {draft === null ? (
        agenda !== null ? (
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {agenda}
          </Typography>
        ) : (
          <Typography sx={{ color: tokens.text2 }}>{t('meetings.session.agendaEmpty')}</Typography>
        )
      ) : (
        <Stack spacing={1}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('meetings.session.agenda')}
            value={draft}
            onChange={(event) => setDraft(event.target.value.slice(0, AGENDA_MAX))}
            multiline
            minRows={3}
            fullWidth
          />
          <Stack direction="row" sx={{ gap: 1 }}>
            <SubmitButton onClick={() => void handleSave()} loading={saving} disabled={saving}>
              {t('common.save')}
            </SubmitButton>
            <Button onClick={() => setDraft(null)} disabled={saving}>
              {t('common.cancel')}
            </Button>
          </Stack>
        </Stack>
      )}
    </HeadedCard>
  );
}
